import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import axios from 'axios';
import React, { FC } from 'react';
import { isMobile } from 'react-device-detect';
import { FormattedMessage } from 'react-intl';
import { useQuery } from 'react-query';
import { Redirect } from 'react-router';
import { Button } from 'components/atoms/button';
import { triggerOtp } from 'components/pages/two-factor-auth/utils';
import { RoutePaths } from 'constant';
import { useQueryParams } from 'hooks/use-query-params';
import { ApiKey } from 'services';
import { getTokenMeta, PermissionType } from 'utils/token';
import { ErrorIcon, FullPageResult, LoadingIcon, SuccessIcon } from './styles';

export const LinkActionPage: FC = () => {
  const queryParams = useQueryParams();
  const api_url = queryParams.get('api_url') || '';
  const splitApi = api_url.split('?');
  const apiUrlParams = new URLSearchParams(splitApi[1]);
  const api_token = apiUrlParams.get('token') ?? '';
  const { decoded } = getTokenMeta(api_token);

  // action_context_id is something we used to deduce what text to show
  // on every possible scenarios.
  // For ex: the action_context_id will be 1002 for task in progress,
  // and we show messages linked to that context.
  const action_context_id = queryParams.get('action_context_id') || '';
  const primary_link = queryParams.get('primary_link') || '';

  const isOtpRequired = decoded?.scopes?.includes(PermissionType.UserOtp);
  const isLoggedIn = ApiKey.exists();
  const shouldRedirectToOtpPage = isOtpRequired && !isLoggedIn;
  const { data: response, status } = useQuery(
    'link-action',
    () =>
      axios.post<string, { data: { taskName: string } }>(api_url, undefined, {
        headers: ApiKey.header(),
      }),
    {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: !shouldRedirectToOtpPage,
    },
  );
  const taskName = response?.data.taskName ?? '';

  if (shouldRedirectToOtpPage) {
    triggerOtp(api_token);

    return (
      <Redirect
        to={{
          pathname: RoutePaths.magicLink2fa,
          state: {
            token: api_token,
            taskStatusUpdateAction: {
              otpEnabled: true,
              apiUrl: api_url,
              redirectUrl: primary_link,
            },
          },
        }}
      />
    );
  }

  switch (status) {
    case 'loading':
      return (
        <FullPageResult
          title={
            <>
              <FormattedMessage
                // TODO: use context specific message as loading_message can be for phase also when we're doing it.
                id="link_action.loading_message"
              />
              <strong>
                <FormattedMessage
                  id={`link_action.context.${action_context_id}`}
                />
              </strong>
              <FormattedMessage id="link_action.loading_message.postfix" />
            </>
          }
          icon={<LoadingIcon size="large" />}
        />
      );
    case 'error':
      return (
        <FullPageResult
          extra={
            !isMobile ? (
              <Button
                iconPlacement="right"
                type="primary"
                size="large"
                icon={<ArrowRight16 />}
                href={primary_link}
              >
                <FormattedMessage id="link_action.cta" />
              </Button>
            ) : null
          }
          subTitle={<FormattedMessage id="link_action.error_message.extra" />}
          title={
            <>
              <FormattedMessage id="link_action.error_message" />
            </>
          }
          icon={<ErrorIcon />}
        />
      );
    case 'success':
      return (
        <FullPageResult
          extra={
            !isMobile ? (
              <Button
                iconPlacement="right"
                type="primary"
                size="large"
                icon={<ArrowRight16 />}
                href={primary_link}
              >
                <FormattedMessage id="link_action.cta" />
              </Button>
            ) : null
          }
          title={
            <>
              {`"${taskName}"`} <br />
              <strong>
                <FormattedMessage id="link_action.success_message" />
                <FormattedMessage
                  id={`link_action.context.${action_context_id}`}
                />
              </strong>
            </>
          }
          icon={<SuccessIcon />}
        />
      );
    default:
      return null;
  }
};
